<template>
  <div
    class="content-box-wrapper py-12 relative overflow-x-hidden"
    :class="[bgClass]"
  >
    <div
      class="container content-box-grid grid"
      :class="{ 'visual-right': visualRight, 'single-column': !hasVisual }"
    >
      <BlocksContentBoxFullImageAsset v-if="src" :src="optimizeImage" />
      <div
        ref="content"
        class="content-box-content m-0 text-left text-smallest md:text-base font-gt-america font-light text-dark leading-normal md:mx-auto lg:mx-0"
        :style="contentStyle"
      >
        <div
          class="lg:w-9/12 mx-auto content relative flex flex-col items-center"
        >
          <SharedTextSectionHeader :title="title" spacing="bottom" />
          <component
            :is="el.type"
            v-for="(el, index) in bodyText"
            class="mb-6 text-smaller md:text-base"
          >
            <template v-if="el.text">
              {{ el.text }}
            </template>
            <template v-else>
              <li
                v-for="(text, i) in el.items"
                class="pl-6 relative mb-2"
              >
                {{ text }}
              </li>
            </template>
          </component>
          <div class="block mt-8 mx-auto lg:mx-0 text-center lg:text-left">
            <MajorityUiMajorityLink
              v-if="to && cta"
              :to="to"
              class="btn"
              :class="[btnVariantClass]"
              data-test-id="button-box-expandable-cta"
              tracking-name="contentBoxExpandableButton"
              tracking-type="cta"
              :text="cta"
            />
            <button
              v-else-if="cta"
              class="btn"
              :class="[btnVariantClass]"
              data-test-id="button-box-expandable-cta"
              @click="onActionTigger"
            >
              {{ cta }}
            </button>
          </div>
          <div
            v-if="isFold"
            class="w-full h-10 bg-gradient-to-t from-bright-yellow -mt-8 z-50 absolute bottom-0"
          />
        </div>
        <button
          v-if="isFold"
          class="btn btn-black-outline mx-auto block mt-8"
          @click="unfold"
        >
          {{ $messages("action.read_more") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { optimize } from '@/utils/image-optimization'

export default {
  props: {
    src: {
      type: String,
      default: () => null
    },
    title: {
      type: String,
      required: true
    },
    body: {
      type: [String, Array],
      required: true,
      default: ''
    },
    cta: {
      type: String,
      default: () => null
    },
    to: {
      type: [String, Object],
      default: () => ''
    },
    bgClass: {
      type: String,
      default: () => 'bg-bright-yellow',
      validator: val => !!val.match(new RegExp(/bg-[a-z-]+/, 'g'))
    },
    btnVariantClass: {
      type: String,
      default: () => 'btn-black',
      validator: val => !!val.match(new RegExp(/btn-[a-z-]+/, 'g'))
    },
    visualRight: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      visualFirst: true,
      isFold: false,
      initialHeight: 'auto'
    }
  },
  computed: {
    contentStyle () {
      return {
        '--content-box-content-height': this.isFold
          ? '210px'
          : this.initialHeight + 'px'
      }
    },
    hasVisual () {
      return !!this.src
    },
    bodyText () {
      const result = []
      const chunks = Array.isArray(this.body)
        ? this.body
        : this.body.split('\n')
      chunks.forEach((e) => {
        if (e.startsWith('+') || e.startsWith('*')) {
          const type = e.startsWith('+') ? 'ol' : 'ul'
          const text = e.substring(1).trim()

          if (result.length === 0 || result[result.length - 1].type !== type) {
            result.push({ type, items: [text] })
          } else if (result[result.length - 1].type === type) {
            result[result.length - 1].items.push(text)
          }
        } else {
          result.push({ type: 'p', text: e })
        }
      })
      return result
    },
    optimizeImage () {
      return optimize(this.src, false, { w: 1920 })
    }
  },
  mounted () {
    this.initialHeight = getComputedStyle(this.$refs.content).height.match(
      new RegExp(/[0-9]+/, 'g')
    )
    this.isFold = this.$device.isMobile && this.initialHeight > 200
  },
  methods: {
    unfold () {
      this.isFold = false
    },
    onLinkTrigger () {
      const localRoute = this.$mRouter.resolveLocalRoute(this.to)
      this.$track.linkClicked('contentBoxExpandableButton', 'cta', {
        linkText: this.cta,
        linkRouteKey: localRoute.route.meta.key,
        linkUrl: localRoute.href
      })
    },
    onActionTigger () {
      this.$emit('action')
      this.$track.linkClicked('contentBoxExpandableButton', 'cta', {
        linkText: this.cta
      })
    }
  }
}
</script>

<style lang="postcss" scoped>
.content-box-header {
  grid-area: header;
  align-self: end;
}
.content-box-visual {
  grid-area: visual;
}
.content-box-content {
  grid-area: content;

  .content {
    height: var(--content-box-content-height);
    transition: height 0.2s ease-in;
    overflow: hidden;

    @screen md {
      height: auto;
    }
  }
}

.content-box-grid {
  grid-template-columns: 1fr;
  grid-template-rows: auto auto auto;
  row-gap: 2rem;
  grid-template-areas: "visual" "header" "content";
}

@screen lg {
  .content-box-grid {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    grid-template-areas: "visual header" "visual content";

    &.visual-right {
      grid-template-areas: "header visual" "content visual";
    }

    &.single-column {
      grid-template-columns: 1fr;
      grid-template-areas: "header" "content";
    }
  }
}

ol,
ul {
  counter-reset: content-box-list-counter;
  li {
    counter-increment: content-box-list-counter;
    &::before {
      @apply w-em mr-0 absolute left-0;
    }
  }
}
ol li::before {
  content: counter(content-box-list-counter) ".";
  @apply font-druk text-dark;
}

ul li::before {
  content: "\e00c";
  font-family: "majority-dsm";
  margin-top: 2px;
  @apply text-dark text-tiny;
}
</style>
